import { useState, useMemo } from 'react';
import type { SortingState, PaginationState } from '@hs/ui';
import { Button, Chip } from '@heroui/react';
import { IconRefresh as RefreshCw } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { PageDataGrid, PageDataGridActionToolbar } from '@/components/layouts/page/PageDataGrid';

type Subscription = {
  id: string;
  status: string;
  seatQuantity?: number;
  startedAt?: string;
  endedAt?: string | null;
  renewalMode?: string;
  createdAt?: string;
};

const SUB_SORT_FIELDS = new Set(['startedAt', 'endedAt', 'status', 'createdAt']);

const STATUS_MAP: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'danger' }> = {
  TRIAL: { label: '试用', color: 'warning' },
  ACTIVE: { label: '生效中', color: 'success' },
  PAST_DUE: { label: '逾期', color: 'danger' },
  SUSPENDED: { label: '已暂停', color: 'warning' },
  CANCELED: { label: '已取消', color: 'default' },
  EXPIRED: { label: '已过期', color: 'danger' },
};

function formatDate(dateString: string | null | undefined) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function SubscriptListPanel() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [enumFilters, setEnumFilters] = useState<Record<string, string>>({});

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);

  const sortItem = sorting[0];
  const sortBy =
    sortItem && SUB_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as 'startedAt' | 'endedAt' | 'status' | 'createdAt')
      : undefined;

  const { data, isLoading, refetch } = trpc.bizSubscription.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortBy
        ? {
            sortBy,
            sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const),
          }
        : {}),
      ...(enumFilters.status
        ? {
            status: enumFilters.status as
              | 'TRIAL'
              | 'ACTIVE'
              | 'PAST_DUE'
              | 'SUSPENDED'
              | 'CANCELED'
              | 'EXPIRED',
          }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const subscriptions = (data?.items ?? []) as Subscription[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const columnOverrides = useMemo(
    () => ({
      status: {
        cell: (row: Subscription) => {
          const s = STATUS_MAP[row.status] ?? { label: row.status, color: 'default' as const };
          return (
            <Chip color={s.color} size="sm" variant="soft">
              <Chip.Label>{s.label}</Chip.Label>
            </Chip>
          );
        },
      },
      renewalMode: {
        cell: (row: Subscription) => (
          <Chip color={row.renewalMode === 'AUTO' ? 'success' : 'default'} size="sm" variant="soft">
            <Chip.Label>{row.renewalMode === 'AUTO' ? '自动' : '手动'}</Chip.Label>
          </Chip>
        ),
      },
      startedAt: { cell: (row: Subscription) => formatDate(row.startedAt) },
      endedAt: {
        cell: (row: Subscription) => (row.endedAt ? formatDate(row.endedAt) : '永久'),
      },
      createdAt: { cell: (row: Subscription) => formatDate(row.createdAt) },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <PageDataGrid<Subscription>
        className="flex min-h-0 min-w-0 flex-1 flex-col"
        source="subscription"
        data={subscriptions}
        recordCount={total}
        pageCount={pageCount}
        isLoading={isLoading}
        emptyMessage="暂无数据"
        getRowId={(row) => row.id}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        enumFilters={enumFilters}
        onEnumFiltersChange={setEnumFilters}
        columnOverrides={columnOverrides}
        metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
        paginationBar={{}}
      >
        <PageDataGridActionToolbar>
          <Button variant="secondary" onPress={() => void refetch()}>
            <RefreshCw className="size-4" stroke={1.5} />
            刷新
          </Button>
        </PageDataGridActionToolbar>
      </PageDataGrid>
    </div>
  );
}
